import type { components } from '@/api'
import type { LibraryItem } from '../Library/libraryStore'

/** The response to GET /cases/:caseId, containing all properties of a case. */
export type CaseResponse = components['schemas']['Cases.CaseResponse']
export type MessageResponse = CaseResponse['messages'][number]
export type OutputResponse = CaseResponse['outputs'][number]

/** The response to GET /cases, containing only partial case data. */
export type PaginatedCaseResponse = components['schemas']['Cases.CaseResponseInList']

export type PaginatedCase = {
  id: string
  createdAt: string
  updatedAt: string
  name: string
  userId: string
  errorReason: string | null
  outputs: CaseOutput[]
  status: CaseStatus
  isUnread: boolean
  workspaceId: string
}

/**
 * The `Message` type is a discriminated union, all members have these
 * properties.
 */
type MessageBase = {
  id: string
  createdAt: string
  updatedAt: string
  isUnread: boolean
}

/**
 * An in-progress response from the server. It will receive websocket updates
 * and eventually be replaced by a `MessageFullResponse`.
 */
export type MessagePartialResponse = MessageBase & {
  type: 'partial_response'
  text: string
  sources: CaseMessageSource[]
  suggestions: string[]
}

/**
 * A full response from the server. This is the final state of a response message.
 */
export type MessageFullResponse = MessageBase & {
  type: 'response'
  text: string
  sources: CaseMessageSource[]
  suggestions: string[]
}

/** A message sent by a user */
export type MessageQuery = MessageBase & {
  type: 'query'
  fileIds: string[]
  text: string
  userId: string
  agentId: string | null
  hubId: string | null
}

/** A message that points to an output of an agent run on the case. */
export type MessageAgentRun = MessageBase & {
  type: 'agent_run'
  outputId: string | null
  agentId: string
  inProgress: boolean
}

export type MessageToolRun = MessageBase & {
  type: 'tool_run'
  toolName: ToolName
  inProgress: boolean
}

type Message =
  | MessagePartialResponse
  | MessageFullResponse
  | MessageQuery
  | MessageAgentRun
  | MessageToolRun

export type CaseOutput = {
  createdAt: string
  id: string
  projectId: string
  entityId: string
}

export type CaseSource =
  | { type: 'library-item'; libraryItem: LibraryItem }
  | { type: 'file'; file: { filename: string } }

export type CaseMessageSource = components['schemas']['Cases.Source']

type CaseFileStatus = CaseResponse['files'][number]['status']

export type CaseFile = {
  id: string
  name: string
  status: CaseFileStatus
  url: string | null // Presigned URL for file access
}

export type CaseStatus = CaseResponse['status']

export type Case = {
  id: string
  name: string
  createdAt: string
  updatedAt: string
  projectIds: string[]
  userId: string
  errorReason: string | null
  status: CaseStatus
  messages: Message[]
  outputs: CaseOutput[]
  sources: CaseSource[]
  files: CaseFile[]
  isUnread: boolean
  workspaceId: string
}

export const isFullCase = (c?: PaginatedCase | Case | null): c is Case => !!c && 'messages' in c

export type CaseInputAttachment = {
  uploadId: string
  type: 'file' | 'library-file' | 'integration-file'
  name: string
  fileId: string | null
  progress: number | null
  status:
    | 'registering'
    | 'uploading'
    | 'confirming'
    | 'library-downloading'
    | 'processing'
    | 'error'
    | 'complete'
}

export type BubbleMessagePlaceholder = {
  id: string
  messageType: 'response-placeholder'
  text: string
  createdAt: string
  updatedAt: string
}

export type BubbleSent = { id: string; type: 'sent'; message: BubbleMessageQuery }
export type BubbleReceived = {
  id: string
  type: 'received'
  errorMessage?: string
  messages: (
    | BubbleMessageResponse
    | BubbleMessageAgentRun
    | BubbleMessageToolRun
    | BubbleMessagePlaceholder
  )[]
  previousQueryId: string | null
}

/**
 * A chat bubble in the Case UI. A bubble can either be a query sent by the user,
 * or a group of responses to a user's query.
 */
export type Bubble = BubbleSent | BubbleReceived

export type BubbleMessageResponse = {
  id: string
  messageType: 'partial_response' | 'response' | 'error'
  attachments: CaseInputAttachment[]
  sources?: CaseMessageSource[]
  suggestions: string[]
  text: string
  createdAt: string
  updatedAt: string
}

export type ToolName = components['schemas']['Cases.ToolRunToolName']

export type BubbleMessageToolRun = {
  id: string
  messageType: 'tool_run'
  attachments: CaseInputAttachment[]
  toolName: ToolName
  createdAt: string
  updatedAt: string
  inProgress: boolean
}

export type BubbleMessageQuery = {
  id: string
  messageType: 'query' | 'pending_query'
  agentId: string | null
  attachments: CaseInputAttachment[]
  createdAt: string
  text: string
  updatedAt: string
  user: { id: string; fullName: string | null; email: string | null } | null
}

export type BubbleMessageAgentRun = {
  id: string
  agent: { id: string; name: string; coverHighRes: string; coverLowRes: string } | null
  allMessageIds: string[]
  attachments: CaseInputAttachment[]
  createdAt: string
  inProgress: boolean
  messageType: 'agent_run'
  outputs: { messageId: string; entityId?: string; outputId?: string }[]
  updatedAt: string
}

export type BubbleMessage = BubbleMessageResponse | BubbleMessageQuery | BubbleMessageAgentRun
